import {
  getFirstItem,
  getStartEndOfMonthFormatted,
  RoleType,
  useGetDaysByUserQuery,
  useGetUsersQuery,
  UsersPermissionsUser,
  workedHours
} from "@mc-intranet/shared";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ReactEcharts from "echarts-for-react";
import { useAtomValue } from "jotai";
import moment, { Moment } from "moment";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { authStorageAtom, modeAtom } from "../../atoms";
import { useGraphQLClient } from "../../cache";
import { useGetTimeEntriesByDay } from "../../services";
import { _gaugeOption, _option } from "./options";

const StatsPage = () => {
  const mode = useAtomValue(modeAtom);
  const chart = useRef<any>();
  const authStorage = useAtomValue(authStorageAtom);
  const { user, role } = authStorage;
  const canSeeOtherUsers =
    role === RoleType.account ||
    role === RoleType.manager ||
    role === RoleType.admin;

  const [selectedUser, setSelectedUser] = useState(user);

  const [option, setOption] = useState(_option);
  const [gaugeOption, setGaugeOption] = useState(_gaugeOption);
  const [showMbf, setShowMbf] = useState(false);

  const [state, setState] = useState({
    date: moment(),
    userId: user?.documentId as string,
    redmineId: user?.redmineUserId?.toString() as string,
    startDate: getStartEndOfMonthFormatted(moment()).startOfMonth,
    endDate: getStartEndOfMonthFormatted(moment()).endOfMonth
  });
  const [isDataUnavailable, setIsDataUnavailable] = useState<boolean>(false);
  const [redmineClient, setRedmineClient] = useState<"activities" | "request">(
    "activities"
  );

  const { client } = useGraphQLClient();

  const { data: usersData, isLoading: usersLoading } = useGetUsersQuery(
    client,
    {},
    {
      enabled: !!user,
      onError: (error: any) => {
        toast.error(error?.response?.error.message, {
          position: toast.POSITION.TOP_CENTER
        });
      }
    }
  );
  // console.log("usersData", usersData);

  // console.log("variables :>> ", variables);
  const { data: daysData, isLoading: loadingDays } = useGetDaysByUserQuery(
    client,
    {
      userId: state.userId,
      startDate: state.startDate,
      endDate: state.endDate
    },
    {
      enabled: !!selectedUser?.documentId && !!state.userId,
      onError: (error: any) => {
        toast.error(error?.response?.error.message, {
          position: toast.POSITION.TOP_CENTER
        });
      }
    }
  );

  const { data: redimeData, isLoading } = useGetTimeEntriesByDay({
    userId: state.redmineId,
    startDate: state.startDate,
    endDate: state.endDate,
    client: redmineClient
  });

  const handleUserChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: UsersPermissionsUser
  ) => {
    // console.log(`event`, event);
    // console.log(`value`, value);
    setSelectedUser(value);

    setState((prev) => {
      // console.log(`prev`, prev);
      return {
        ...prev,
        userId: value?.documentId as string,
        redmineId: (value?.redmineUserId as number).toString()
      };
    });
  };

  const handleSelectedDateChange = (date: Moment) => {
    setState((prev) => {
      return {
        ...prev,
        date: date,
        startDate: getStartEndOfMonthFormatted(moment(date)).startOfMonth,
        endDate: getStartEndOfMonthFormatted(moment(date)).endOfMonth
      };
    });
  };

  useEffect(() => {
    if (!!user && usersData && !canSeeOtherUsers) {
      const _user = usersData.usersPermissionsUsers?.find(
        (f) => f?.documentId === user.documentId
      ) as UsersPermissionsUser;
      if (_user) {
        setSelectedUser(_user);
        setState((prev) => {
          return {
            ...prev,
            userId: _user.documentId as string,
            redmineId: (_user?.redmineUserId as number).toString()
          };
        });
      }
    }
  }, [canSeeOtherUsers, user, usersData]);

  useEffect(() => {
    if (redimeData && daysData) {
      // calculating redmine data
      const _redmineHours = getFirstItem(redimeData)?.hours;

      const redmineData = _redmineHours?.reduce(
        (prev: any, current: any) => {
          const _total = showMbf ? current?.mbf : current?.hours;
          return {
            ...prev,
            bar: [...prev.bar, [current?.spent_on, _total]],
            total: prev.total + _total
          };
        },
        {
          bar: [],
          total: 0
        }
      ) ?? {
        bar: [],
        total: 0
      };

      // calculating intranet data
      const intranetData = daysData?.days?.reduce(
        (prev: any, current: any) => {
          return {
            ...prev,
            bar: [
              ...prev.bar,
              [current?.date, workedHours(current).totale]
            ],
            total: prev.total + workedHours(current).totale
          };
        },
        {
          bar: [],
          total: 0
        }
      );

      const percentage =
        redmineData.total <= 0 || intranetData.total <= 0
          ? 0
          : (redmineData.total * 100) / intranetData.total;

      // console.log("redimeData", redimeData);
      // console.log("daysData", daysData);
      // console.log("intranetData", intranetData);
      // console.log("redmineData.total", redmineData.total);
      // console.log("intranetData.total", intranetData.total);
      // console.log("percentage", percentage);

      setIsDataUnavailable(redmineData.total <= 0 && intranetData.total <= 0);

      // setting charts
      setOption((prev) => {
        const seriesIntra = { ...prev.series[0], data: intranetData.bar };
        const seriesRedimine = { ...prev.series[1], data: redmineData.bar };

        return {
          ...prev,
          series: [seriesIntra, seriesRedimine]
        };
      });

      // setPieOption((prev) => {
      //   const data = [
      //     { value: intranetData.total, name: "Intranet" },
      //     { value: redmineData.total, name: "Redmine" }
      //   ];

      //   console.log("data", data);

      //   const series = { ...prev.series[0], data } as any;
      //   return {
      //     ...prev,
      //     series: [series]
      //   };
      // });
      setGaugeOption((prev) => {
        const data = [{ value: percentage }];
        return {
          ...prev,
          series: [{ ...prev.series[0], data }]
        };
      });
      // console.log("redimeData", redimeData);
      // console.log("daysData", daysData);
    }
  }, [daysData, redimeData, showMbf, state.startDate]);

  const mbfChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowMbf(event.target.checked);
  };
  const clientChange = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    value: any
  ) => {
    if (value) setRedmineClient(value);
  };

  return (
    <Stack direction={"column"} height={"100%"}>
      <Box
        className="switch-user"
        display="flex"
        alignItems="center"
        justifyContent="center"
        paddingY={3}
      >
        {canSeeOtherUsers && usersData ? (
          <>
            <span>Cambia utente</span>
            {!usersLoading &&
              getFirstItem(usersData?.usersPermissionsUsers) && (
                <Autocomplete
                  disablePortal
                  id="combo-box-demo"
                  sx={{
                    bgcolor: "background.default",
                    marginInline: 2,
                    width: 200
                  }}
                  disableClearable={true}
                  size="small"
                  value={selectedUser as UsersPermissionsUser}
                  options={
                    (usersData?.usersPermissionsUsers as UsersPermissionsUser[]) ??
                    []
                  }
                  getOptionLabel={(option) =>
                    `${option?.firstName} ${option?.lastName}`
                  }
                  isOptionEqualToValue={(option, value) => {
                    return option.documentId === value.documentId;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Utente"
                      placeholder="Utente"
                    />
                  )}
                  onChange={handleUserChange}
                />
              )}
          </>
        ) : (
          <Typography
            marginRight={2}
          >{`${user?.firstName} ${user?.lastName}`}</Typography>
        )}
        <DatePicker
          views={["year", "month"]}
          label={"Mese e Anno"}
          openTo={"month"}
          slotProps={{ textField: { size: "small" } }}
          value={state.date}
          disableFuture
          onChange={(newDate) => {
            handleSelectedDateChange(newDate as Moment);
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showMbf}
              inputProps={{ "aria-label": "controlled" }}
              size="small"
              onChange={mbfChange}
            />
          }
          label="MBF"
          labelPlacement="top"
        />
        <ToggleButtonGroup
          color="primary"
          value={redmineClient}
          exclusive
          onChange={clientChange}
          aria-label="redmine-client"
          defaultValue={"activities"}
          // sx={{ backgroundColor: mode === "dark" ? "#001529" : "#fff" }}
        >
          <ToggleButton value="activities" size="small">
            Activities
          </ToggleButton>
          <ToggleButton value="request" size="small">
            Request
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {/* {isDataAvailable && redimeData && daysData && ( */}
      {!isDataUnavailable && redimeData && daysData && (
        <Grid
          container
          spacing={2}
          sx={{ height: "100%" }}
          flexDirection={{
            xs: "column",
            md: "row"
          }}
        >
          <Grid item md={8} sx={{ width: "100%" }} flex={1}>
            <ReactEcharts
              ref={chart}
              style={{ height: "100%" }}
              option={{
                ...(option as any),

                backgroundColor: mode === "dark" ? "#001122" : "#efefef"
              }}
              theme={mode}
            />
          </Grid>
          <Grid
            item
            md={4}
            sx={{
              width: "100%"
            }}
            flex={1}
          >
            <ReactEcharts
              ref={chart}
              style={{ height: "100%" }}
              option={{
                ...(gaugeOption as any),

                backgroundColor: mode === "dark" ? "#001122" : "#efefef"
              }}
              theme={mode}
            />
          </Grid>
        </Grid>
      )}
      {isDataUnavailable && (
        <Box
          flex={1}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Typography component="h2" textAlign={"center"}>
            Nessun dato disponibile per il periodo selezionato
          </Typography>
        </Box>
      )}
      {(loadingDays || isLoading) && (
        <Box
          position={"absolute"}
          sx={{ inset: 0 }}
          bgcolor="rgba(0, 0, 0, 0.5)"
          display={"flex"}
          alignItems="center"
          justifyContent={"center"}
        >
          <CircularProgress />
        </Box>
      )}
    </Stack>
  );
};

export default StatsPage;
